import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useException } from '@erikdakoda/ui/hooks/useException';
import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';
import { doFetch } from '@erikdakoda/ui/shared/doFetch';
import { type CloudinaryUploadWidgetResult, type CloudinaryUploadWidgetOptions } from '../types';
import '../styles/uploadWidget.css';
import { useTheme } from '@mui/material/styles';

interface UseCloudinaryUploadWidgetProps {
  uploadWidgetConfig: Partial<CloudinaryUploadWidgetOptions>;
}

interface UseCloudinaryUploadWidgetResult {
  isUploadWidgetLoading: boolean;
  openUploadWidget: (
    setPublicIds: Dispatch<SetStateAction<string | string[] | undefined>>,
    currentPublicIds?: string | string[],
  ) => void;
}

export function useCloudinaryUploadWidget({
  uploadWidgetConfig,
}: UseCloudinaryUploadWidgetProps): UseCloudinaryUploadWidgetResult {
  const { t } = useTranslation('cloudinary-ui');
  const exception = useException();
  const { user } = useCurrentUser();
  const [loaded, setLoaded] = useState(!!window.cloudinary);
  const theme = useTheme();

  const fullUploadWidgetConfig: CloudinaryUploadWidgetOptions = {
    uploadPreset: 'default',
    maxImageFileSize: 1024 * 1024 * 5, // 5MB
    cropping: false,
    multiple: false,
    sources: ['local', 'url', 'camera', 'dropbox', 'google_drive'],
    resourceType: 'image',
    styles: {
      palette: {
        window: theme.palette.background.menu,
        windowBorder: theme.palette.text.secondary,
        tabIcon: theme.palette.primary.dark,
        inactiveTabIcon: theme.palette.text.disabled,
        menuIcons: theme.palette.text.secondary,
        textDark: '#000000',
        textLight: '#FFFFFF',
        link: theme.palette.primary.main,
        action: theme.palette.primary.main,
        error: theme.palette.error.main,
        inProgress: theme.palette.info.main,
        complete: theme.palette.success.main,
        sourceBg: theme.palette.background.menu,
      },
      frame: {
        background: theme.palette.background.backdrop,
      },
      ...uploadWidgetConfig.styles,
    },
    ...uploadWidgetConfig,
    cloudName: process.env.NEXT_PUBLIC_CLOUDINARY_NAME || '',
    apiKey: process.env.NEXT_PUBLIC_CLOUDINARY_API_KEY || '',
    ...(user ? { context: { uploader: user.id } } : {}),
    uploadSignature: (callback, paramsToSign) => {
      void doFetch<{ signature?: string }>({
        url: '/api/cloudinary/sign-upload',
        method: 'POST',
        params: { paramsToSign },
        exception,
      }).then((signed) => {
        if (typeof signed?.signature === 'string' && signed.signature.length > 0) {
          callback(signed.signature);
        }
      });
    },
  };

  useEffect(() => {
    if (window.cloudinary) {
      setLoaded(true);
      return;
    }
    const existingScript = document.getElementById('uw');
    const script =
      existingScript instanceof HTMLScriptElement
        ? existingScript
        : document.createElement('script');
    const markLoaded = () => setLoaded(true);
    script.addEventListener('load', markLoaded);
    if (!existingScript) {
      script.setAttribute('async', '');
      script.setAttribute('id', 'uw');
      script.src = 'https://upload-widget.cloudinary.com/global/all.js';
      document.body.appendChild(script);
    }
    return () => {
      script.removeEventListener('load', markLoaded);
    };
  }, []);

  const openUploadWidget = (
    setPublicIds: Dispatch<SetStateAction<string | string[] | undefined>>,
    currentPublicIds?: string | string[],
  ) => {
    if (!user) {
      exception(t('cloudinaryNotSignedIn'));
      return;
    }
    if (!window.cloudinary) {
      exception(t('cloudinaryScriptNotLoaded'));
      return;
    }
    currentPublicIds = currentPublicIds
      ? Array.isArray(currentPublicIds)
        ? currentPublicIds
        : [currentPublicIds]
      : [];
    const newPublicIds: string[] = [];

    const uploadWidget = window.cloudinary.createUploadWidget(
      fullUploadWidgetConfig,
      (error: unknown, result: CloudinaryUploadWidgetResult) => {
        if (error) {
          exception(error, t('cloudinaryUploadError'));
          return;
        } else if (result?.event === 'success') {
          const publicId = result.info.public_id;
          void doFetch({
            url: '/api/cloudinary/record-image',
            method: 'POST',
            params: { publicId },
            exception,
          }).catch(() => {
            // doFetch already reports its own errors through `exception`; nothing is left to handle.
          });
          newPublicIds.push(publicId);
        } else if (result?.event === 'close') {
          if (newPublicIds.length > 0) {
            const updatedValue = [...currentPublicIds, ...newPublicIds];
            if (fullUploadWidgetConfig.multiple) {
              setPublicIds(updatedValue);
            } else {
              setPublicIds(updatedValue[0]);
            }
          }
        }
      },
    );
    uploadWidget.open();
  };

  return {
    isUploadWidgetLoading: !loaded,
    openUploadWidget,
  };
}
